import { Button } from '@/shared/components/ui/button'
import { Card, CardContent } from '@/shared/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { TooltipProvider } from '@/shared/components/ui/tooltip'
import { cn } from '@/shared/lib/utils'
import { Character, CharacterGenerationOptions, CharacterVariant } from '@storyverse/shared'
import { Activity, Brain, Image as ImageIcon, Layers, User } from 'lucide-react'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useDeleteCharacterMutation, useGenerateLookTestMutation, useSaveCharacterMutation } from '../queries/character.queries'

// Modular Components
import { CharacterCoreIdentity } from './form/CharacterCoreIdentity'
import { CharacterVariantSelector } from './form/CharacterVariantSelector'
import { VariantIdentityTab } from './form/VariantIdentityTab'
import { VariantPersonalityTab } from './form/VariantPersonalityTab'
import { VariantPhysicalTab } from './form/VariantPhysicalTab'
import { VariantVisualsTab } from './form/VariantVisualsTab'

interface CharacterFormProps {
    character?: Character | null
    projectId: string
    onClose?: () => void
    onSavingChange?: (isSaving: boolean) => void
}

export const CharacterForm = forwardRef<{ submit: () => void, delete: () => void }, CharacterFormProps>(
    ({ character, projectId, onClose, onSavingChange }, ref) => {
        const saveMutation = useSaveCharacterMutation()
        const deleteMutation = useDeleteCharacterMutation()

        useEffect(() => {
            onSavingChange?.(saveMutation.isPending)
        }, [saveMutation.isPending, onSavingChange])

        const [formData, setFormData] = useState<Partial<Character>>({
            fullname: '',
            nickname: '',
            title: '',
            gender: '',
            core_backstory: '',
            variants: []
        })

        const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null)
        const generateMutation = useGenerateLookTestMutation()
        const fileInputRef = useRef<HTMLInputElement>(null)
        const [uploadPreview, setUploadPreview] = useState<Record<string, string>>({})
        const [generationOptions, setGenerationOptions] = useState<CharacterGenerationOptions>({
            userInstruction: '',
            referenceImage: ''
        })
        const referencePhotoInputRef = useRef<HTMLInputElement>(null)
        const [referencePhotoPreview, setReferencePhotoPreview] = useState<string | null>(null)
        const [imageMode, setImageMode] = useState<'upload' | 'ai'>('ai')

        const getVariantImageAssetId = (variantId: string, imageType: 'ai' | 'user') => {
            const characterId = formData.id || 'draft-character'
            return `${characterId}-${variantId}-${imageType}`
        }

        useEffect(() => {
            const newPreviews: Record<string, string> = {}
            const objectUrls: string[] = []

            if (character) {
                setFormData(character)
                if (character.variants && character.variants.length > 0) {
                    const defaultVariant = character.variants.find(v => v.isDefault) || character.variants[0]
                    setSelectedVariantId(defaultVariant.id)

                    // Also initialize previews for any existing blobs
                    character.variants.forEach(variant => {
                        const img = variant.user_image as any
                        if (img && (img instanceof Blob || img instanceof File)) {
                            const url = URL.createObjectURL(img)
                            newPreviews[variant.id] = url
                            objectUrls.push(url)
                        }
                    })
                }
            } else {
                const characterId = crypto.randomUUID()
                const initialVariant: CharacterVariant = {
                    id: crypto.randomUUID(),
                    character_id: characterId,
                    label: 'Default',
                    isDefault: true,
                    role: 'Protagonist'
                }
                setFormData({
                    id: characterId,
                    fullname: '',
                    nickname: '',
                    title: '',
                    gender: '',
                    core_backstory: '',
                    variants: [initialVariant]
                })
                setSelectedVariantId(initialVariant.id)
            }

            if (Object.keys(newPreviews).length > 0) {
                setUploadPreview(prev => ({ ...prev, ...newPreviews }))
            }

            return () => {
                objectUrls.forEach(url => URL.revokeObjectURL(url))
            }
        }, [character])

        useImperativeHandle(ref, () => ({
            submit: handleSave,
            delete: handleDelete
        }))

        const handleSave = () => {
            if (!formData.fullname) {
                toast.error('Full Name is required')
                return
            }
            saveMutation.mutate({
                ...formData,
                projectId: projectId,
            } as Character, {
                onSuccess: () => {
                    onClose?.()
                }
            })
        }

        const handleDelete = () => {
            if (character?.id && window.confirm('Are you sure you want to delete this character?')) {
                deleteMutation.mutate(character.id)
                onClose?.()
            }
        }

        const addVariant = () => {
            const newVariant: CharacterVariant = {
                id: crypto.randomUUID(),
                character_id: formData.id || '',
                label: `Variant ${(formData.variants?.length || 0) + 1}`,
                isDefault: false,
                role: 'Supporting'
            }
            setFormData(prev => ({
                ...prev,
                variants: [...(prev.variants || []), newVariant]
            } as Partial<Character>))
            setSelectedVariantId(newVariant.id)
        }

        const updateVariant = (id: string, updates: Partial<CharacterVariant>) => {
            setFormData(prev => ({
                ...prev,
                variants: prev.variants?.map((v: CharacterVariant) => v.id === id ? { ...v, ...updates } : v)
            } as Partial<Character>))
        }

        const selectedVariant = formData.variants?.find((v: CharacterVariant) => v.id === selectedVariantId)

        const validation = useMemo(() => {
            const hasName = !!formData.fullname?.trim()
            const hasGender = !!formData.gender?.trim()
            const hasAge = !!selectedVariant?.age?.trim()

            const physicalTraits = [
                selectedVariant?.hair,
                selectedVariant?.skin_tone,
                selectedVariant?.body_type,
                selectedVariant?.eye_color
            ].filter(t => !!t?.trim())

            const hasEnoughTraits = physicalTraits.length >= 2

            return {
                isReady: hasName && hasGender && hasAge && hasEnoughTraits,
                missingName: !hasName,
                missingGender: !hasGender,
                missingAge: !hasAge,
                missingTraits: !hasEnoughTraits,
                traitCount: physicalTraits.length
            }
        }, [formData.fullname, formData.gender, selectedVariant])

        const handleGenerateImage = async () => {
            if (!selectedVariant) return

            if (!validation.isReady) {
                let message = 'Please fill in required fields: '
                const missingFields = []
                if (validation.missingName) missingFields.push('Full Name')
                if (validation.missingGender) missingFields.push('Gender')
                if (validation.missingAge) missingFields.push('Age')
                if (validation.missingTraits) missingFields.push('at least 2 physical traits')

                toast.error(message + missingFields.join(', '))
                return
            }

            generateMutation.mutate({
                characterId: getVariantImageAssetId(selectedVariant.id, 'ai'),
                character: formData as Character,
                variant: selectedVariant,
                options: generationOptions
            }, {
                onSuccess: (data) => {
                    const updates: any = { look_test_image: data.imageUrl }
                    if (!selectedVariant.user_image) {
                        updates.active_image_type = 'ai'
                    }
                    updateVariant(selectedVariant.id, updates)
                }
            })
        }

        const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0]
            if (!file || !selectedVariant) return

            const previewUrl = URL.createObjectURL(file)
            setUploadPreview(prev => ({ ...prev, [selectedVariant.id]: previewUrl }))

            const updates: any = { user_image: file as any }
            if (!selectedVariant.look_test_image) {
                updates.active_image_type = 'user'
            }
            updateVariant(selectedVariant.id, updates)
        }

        const handleReferencePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0]
            if (!file) return

            const previewUrl = URL.createObjectURL(file)
            setReferencePhotoPreview(previewUrl)

            const reader = new FileReader()
            reader.onloadend = () => {
                setGenerationOptions(prev => ({
                    ...prev,
                    referenceImage: reader.result as string
                }))
            }
            reader.readAsDataURL(file)
        }

        const handleRemoveReferencePhoto = () => {
            setReferencePhotoPreview(null)
            setGenerationOptions(prev => ({
                ...prev,
                referenceImage: ''
            }))
            if (referencePhotoInputRef.current) {
                referencePhotoInputRef.current.value = ''
            }
        }

        const handleRemoveImage = (type: 'ai' | 'user') => {
            if (!selectedVariant) return
            if (type === 'ai') {
                updateVariant(selectedVariant.id, { look_test_image: '' })
            } else {
                setUploadPreview(prev => {
                    const next = { ...prev }
                    delete next[selectedVariant.id]
                    return next
                })
                updateVariant(selectedVariant.id, { user_image: '' })
            }
        }

        const handleSetActiveImage = (type: 'ai' | 'user') => {
            if (!selectedVariant) return
            updateVariant(selectedVariant.id, { active_image_type: type })
        }

        return (
            <TooltipProvider>
                <div className="space-y-10 pb-20">
                    <CharacterCoreIdentity formData={formData} setFormData={setFormData} />

                    <div className="space-y-4">
                        <CharacterVariantSelector
                            variants={formData.variants}
                            selectedVariantId={selectedVariantId}
                            onSelectVariant={setSelectedVariantId}
                            onAddVariant={addVariant}
                        />

                        {selectedVariant && (
                            <Card className="overflow-hidden border-border/50 shadow-lg bg-muted/5 animate-in fade-in slide-in-from-top-4 duration-500">
                                <CardContent className="p-6 space-y-8">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">
                                                Variant Version Name
                                            </label>
                                            <input
                                                value={selectedVariant.label}
                                                onChange={(e) => updateVariant(selectedVariant.id, { label: e.target.value })}
                                                className="w-full bg-background border-primary/20 h-10 px-3 rounded-md font-bold text-lg focus:outline-none focus:ring-1 focus:ring-primary/40"
                                            />
                                        </div>
                                        <div className="flex items-end justify-end space-x-4">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        variants: prev.variants?.map((v: CharacterVariant) => ({
                                                            ...v,
                                                            isDefault: v.id === selectedVariant.id
                                                        }))
                                                    } as Partial<Character>))
                                                }}
                                                disabled={selectedVariant.isDefault}
                                                className={cn("h-10 px-4 rounded-full", selectedVariant.isDefault && "bg-primary/10 text-primary border-primary/20 pointer-events-none")}
                                            >
                                                {selectedVariant.isDefault ? 'Current Default' : 'Set as Default'}
                                            </Button>
                                        </div>
                                    </div>

                                    <Tabs defaultValue="identity" className="w-full">
                                        <div className="overflow-x-auto -mx-1 px-1 mb-2 scrollbar-hide">
                                            <TabsList className="flex bg-muted/40 p-1 h-12 rounded-xl w-full">
                                                <TabsTrigger value="identity" className="flex-1 min-w-[100px] rounded-lg gap-2 data-[state=active]:shadow-sm">
                                                    <User className="h-4 w-4" /> Identity
                                                </TabsTrigger>
                                                <TabsTrigger value="physical" className="flex-1 min-w-[100px] rounded-lg gap-2 data-[state=active]:shadow-sm">
                                                    <Activity className="h-4 w-4" /> Appearance
                                                </TabsTrigger>
                                                <TabsTrigger value="personality" className="flex-1 min-w-[100px] rounded-lg gap-2 data-[state=active]:shadow-sm">
                                                    <Brain className="h-4 w-4" /> Personality
                                                </TabsTrigger>
                                                <TabsTrigger value="images" className="flex-1 min-w-[100px] rounded-lg gap-2 data-[state=active]:shadow-sm">
                                                    <ImageIcon className="h-4 w-4" /> Images
                                                </TabsTrigger>
                                            </TabsList>
                                        </div>

                                        <TabsContent value="identity">
                                            <VariantIdentityTab variant={selectedVariant} updateVariant={updateVariant} />
                                        </TabsContent>

                                        <TabsContent value="physical">
                                            <VariantPhysicalTab variant={selectedVariant} updateVariant={updateVariant} />
                                        </TabsContent>

                                        <TabsContent value="personality">
                                            <VariantPersonalityTab variant={selectedVariant} updateVariant={updateVariant} />
                                        </TabsContent>

                                        <TabsContent value="images">
                                            <VariantVisualsTab
                                                variant={selectedVariant}
                                                handleGenerateImage={handleGenerateImage}
                                                handleFileUpload={handleFileUpload}
                                                handleReferencePhotoUpload={handleReferencePhotoUpload}
                                                handleRemoveReferencePhoto={handleRemoveReferencePhoto}
                                                handleRemoveImage={handleRemoveImage}
                                                handleSetActiveImage={handleSetActiveImage}
                                                generationOptions={generationOptions}
                                                setGenerationOptions={setGenerationOptions}
                                                referencePhotoPreview={referencePhotoPreview}
                                                imageMode={imageMode}
                                                setImageMode={setImageMode}
                                                isGenerating={generateMutation.isPending}
                                                isReadyToGenerate={validation.isReady}
                                                missingRequirements={validation}
                                                fileInputRef={fileInputRef}
                                                referencePhotoInputRef={referencePhotoInputRef}
                                                uploadPreview={uploadPreview}
                                            />
                                        </TabsContent>
                                    </Tabs>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </TooltipProvider>
        )
    }
)

CharacterForm.displayName = 'CharacterForm'
